import { createApiClient } from '@aimsos/api-client';
import { tokenService } from '@aimsos/auth';

/**
 * Work Queues talks to three API bases:
 *
 *  - **Platform** (`VITE_API_BASE_URL`) — workspace user directory, token exchange, …
 *  - **GS** (`VITE_API_BASE_URL_GS`) — my-work fan-out, task actions, previews, …
 *  - **Project** (`VITE_API_BASE_URL_PROJECT`) — the `/v0/project/*` aggregate
 *    (tasks, summary, activity, project config); see `services/projectTasks.js`.
 *
 * AUTH SCHEME — verified live against GS on 2026-08-31 (CT-5091): a real internal
 * token was sent both as `Authorization: <token>` (raw, the beta's scheme) and
 * `Authorization: Bearer <token>` — BOTH returned HTTP 200. GS's authorizer
 * strips/ignores the `Bearer` prefix, so the shared `createApiClient` works as-is.
 *
 * The bearer token is the internal token from {@link tokenService} (Auth0 bridge →
 * `POST /v1/token` exchange, stored as `api_access_token`), injected per-request.
 * When embedded in Core Shell we reuse that single session — we do NOT mount a
 * second Auth0Provider (embed contract).
 *
 * Clients are lazy so `window.__AIMS_SHELL__` (set by WorkQueuesEmbed) is read on
 * the first request, not at module load. The response interceptor unwraps Axios and
 * returns the raw envelope — pass it through `validateApiResponse` before use.
 */

function resolveBaseUrl(shellKey, envKey) {
  const shell = typeof window !== 'undefined' ? window.__AIMS_SHELL__ || {} : {};
  const raw = shell[shellKey] || import.meta.env[envKey] || '';
  return String(raw).replace(/\/+$/, '');
}

const authOptions = {
  getToken: () => tokenService.getToken(),
  refreshToken: async () => {
    const userUuid = localStorage.getItem('user_uuid') || undefined;
    const tenantUuid = localStorage.getItem('workspace_uuid') || undefined;
    return tokenService.refreshToken(userUuid, tenantUuid);
  },
};

/** @type {ReturnType<typeof createApiClient> | null} */
let gsPair = null;
/** @type {ReturnType<typeof createApiClient> | null} */
let platformPair = null;
/** @type {ReturnType<typeof createApiClient> | null} */
let projectPair = null;

function ensureGsClient() {
  if (!gsPair) {
    gsPair = createApiClient({
      baseURL: resolveBaseUrl('apiBaseUrlGS', 'VITE_API_BASE_URL_GS'),
      ...authOptions,
    });
  }
  return gsPair;
}

function ensurePlatformClient() {
  if (!platformPair) {
    platformPair = createApiClient({
      baseURL: resolveBaseUrl('apiBaseUrl', 'VITE_API_BASE_URL'),
      ...authOptions,
    });
  }
  return platformPair;
}

const ensureProjectClient = () => {
  if (!projectPair) {
    const baseURL = resolveBaseUrl('apiBaseUrlProject', 'VITE_API_BASE_URL_PROJECT');
    // An empty base would send `/v0/project/*` to the shell host and fail as a confusing 404.
    if (!baseURL) {
      throw new Error('Project API base URL is not configured (VITE_API_BASE_URL_PROJECT).');
    }
    projectPair = createApiClient({ baseURL, ...authOptions });
  }
  return projectPair;
};

// Cache the bound methods per underlying client so a property access does not
// allocate a fresh bound function every time (the fan-out touches `gsApi.post`
// ~110×/load) and `gsApi.post === gsApi.post` holds for test spies.
const boundCache = new WeakMap();

function proxyClient(getPair) {
  return new Proxy(
    {},
    {
      get(_, prop) {
        const { client } = getPair();
        const value = client[prop];
        if (typeof value !== 'function') return value;
        let bound = boundCache.get(client);
        if (!bound) {
          bound = new Map();
          boundCache.set(client, bound);
        }
        if (!bound.has(prop)) bound.set(prop, value.bind(client));
        return bound.get(prop);
      },
    }
  );
}

/** Governance service — my-work, task, promotion, previews, … */
export const gsApi = proxyClient(ensureGsClient);

/** Platform API — workspace user directory (`listUsersFilter`), … */
export const platformApi = proxyClient(ensurePlatformClient);

/** Project API (`{stage}-aimsos-project-api`, `/v0/project/*`) — tasks, summary, activity, … */
export const projectApi = proxyClient(ensureProjectClient);

export const setToken = (...args) => ensureGsClient().setToken(...args);
